import { financialAccountUserAssociate } from '../../../services/orgs/financialAccountUserAssociate';
import {
  FinancialAccountNode,
  OrgNode,
} from '../../../services/orgs/interfaces';
import { createShortLivedToken } from '../../../services/vault/token';
import { deleteEmptyFields } from '../../../services/vgs/fields';
import { StripeEnv } from '../../../util/enums';
import logger, { logVgsFormSubmitResponse } from '../../../util/logger';
import { createOrgsEntities } from '../../OrgConfirmation/submit/orgsEntities';
import { createProviderEntities } from '../../OrgConfirmation/submit/providerEntities';
import { StripeMetadataInput, SuccessData } from '../../VaultForm';
import { createProviderKycEntities } from './providerKycEntities';
import { requestBodyFormatter } from './requestBodyFormatter';
import { ORG_CONFIRMATION_WITH_CREATE_KYC_SUBMIT_TREATMENT } from '../../../split';
import { VAULT_ORG_CONFIRMATION_WITH_CREATE_KYC_QUERY } from './graphqlQuery';
import VaultSubmitError from '../../../util/VaultSubmitError';
import { generateDescription } from '../../../util/description';
import { BeneficialOwnerData } from '../../BeneficialOwners';

export default async function submit({
  kyb,
  form,
  kycAddressRegion,
  kycTitle,
  kycPercentOwnership,
  kycIsOwner,
  provider,
  submittedOnYourBehalf,
  stripeEnv,
  statementDescriptor,
  metadata,
  org,
  currentUserId,
  financialAccountNode,
  splitTreatments,
  bankAccountPayableOrgId,
  fundraiserId,
  fundraiserSlug,
  beneficialOwnersData,
}: {
  stripeEnv: StripeEnv;
  kyb: Kyb;
  submittedOnYourBehalf: boolean;
  org: OrgNode;
  financialAccountNode: FinancialAccountNode;
  currentUserId: string;
  statementDescriptor?: string;
  metadata?: StripeMetadataInput;
  kycAddressRegion: string;
  kycPercentOwnership: number;
  kycTitle: string;
  kycIsOwner: boolean;
  // after split is removed convert to VGSForm<SuccessData>
  form: VGSForm<{ kycId: string }>;
  provider: VaultProvider;
  splitTreatments: SplitIO.TreatmentsWithConfig;
  bankAccountPayableOrgId?: string;
  fundraiserId?: string | null;
  fundraiserSlug?: string | null;
  beneficialOwnersData: BeneficialOwnerData[];
}): Promise<SuccessData> {
  if (kyb.id === undefined) {
    throw new Error('Kyb does not have an id');
  }
  deleteEmptyFields(form);
  const token = await createShortLivedToken();

  return new Promise((resolve, reject) => {
    if (
      splitTreatments[ORG_CONFIRMATION_WITH_CREATE_KYC_SUBMIT_TREATMENT.name]
        ?.treatment ===
      ORG_CONFIRMATION_WITH_CREATE_KYC_SUBMIT_TREATMENT.treatments.on
    ) {
      form.submit(
        '/graphql',
        {
          data: (formValues: Record<string, string>): VgsRequestBody => ({
            query: VAULT_ORG_CONFIRMATION_WITH_CREATE_KYC_QUERY,
            variables: {
              gateway: provider,
              orgData: {
                accountId: financialAccountNode.acctId,
                financialAccountNodeId: financialAccountNode.finAcctId,
                kybId: kyb.id,
                orgId: org.id,
              },
              rawRepresentativeData: {
                userId: currentUserId,
                kycData: {
                  address: {
                    city: formValues.representativeKycAddressCity,
                    postalCode: formValues.representativeKycAddressPostalCode,
                    region: kycAddressRegion,
                    street: formValues.representativeKycAddressStreet,
                    unit: formValues.representativeKycAddressUnit,
                  },
                  director: false,
                  dob: {
                    day: formValues.representativeKycDobDay,
                    month: formValues.representativeKycDobMonth,
                    year: formValues.representativeKycDobYear,
                  },
                  email: formValues.representativeKycEmail,
                  executive: true,
                  firstName: formValues.representativeKycFirstName,
                  lastName: formValues.representativeKycLastName,
                  owner: kycIsOwner,
                  percentOwnership: kycPercentOwnership,
                  phoneNumber: formValues.representativeKycPhoneNumber,
                  representative: true,
                  ssn: formValues.representativeKycSsn,
                  title: kycTitle,
                },
              },
              submittedOnYourBehalf,
              stripeData: {
                metadata,
                statementDescriptor,
                stripeEnv,
              },
              representativeToReplace: undefined,
              bankAccountPayableOrgId,
              fundraiserId,
              fundraiserSlug,
              productDescription: generateDescription({ org, formValues }),
              rawBeneficialOwners: beneficialOwnersData.map((bo) => ({
                kycData: {
                  address: {
                    city: formValues[
                      `beneficialOwner${bo.internalId}KycAddressCity`
                    ],
                    postalCode:
                      formValues[
                        `beneficialOwner${bo.internalId}KycAddressPostalCode`
                      ],
                    region: bo.addressRegion,
                    street:
                      formValues[
                        `beneficialOwner${bo.internalId}KycAddressStreet`
                      ],
                    unit: formValues[
                      `beneficialOwner${bo.internalId}KycAddressUnit`
                    ],
                  },
                  director: false,
                  dob: {
                    day: formValues[`beneficialOwner${bo.internalId}KycDobDay`],
                    month:
                      formValues[`beneficialOwner${bo.internalId}KycDobMonth`],
                    year: formValues[
                      `beneficialOwner${bo.internalId}KycDobYear`
                    ],
                  },
                  email: formValues[`beneficialOwner${bo.internalId}KycEmail`],
                  executive: false,
                  firstName:
                    formValues[`beneficialOwner${bo.internalId}KycFirstName`],
                  lastName:
                    formValues[`beneficialOwner${bo.internalId}KycFirstName`],
                  owner: true,
                  percentOwnership: bo.percentOwnership,
                  phoneNumber:
                    formValues[`beneficialOwner${bo.internalId}KycPhoneNumber`],
                  representative: false,
                  ssn: formValues[`beneficialOwner${bo.internalId}KycSsn`],
                },
              })),
            },
          }),
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        },
        async (responseStatus, { data, errors }) => {
          logVgsFormSubmitResponse({
            formName: 'OrgConfirmationWithCreateKyc',
            responseStatus,
            data,
            errors,
          });
          if (responseStatus !== 200) {
            throw new Error(`Received ${responseStatus} status.`);
          }

          if (typeof data === 'string') {
            throw new Error(data);
          }

          if (errors !== undefined && errors.length > 0) {
            throw new Error(errors.map((e) => e.message).join(', '));
          }

          // to be removed once split is removed
          const typedData = data as unknown as {
            vaultOrgConfirmationSubmit: {
              successData: SuccessData;
              success: boolean;
              errors: {
                retryable: boolean;
                fieldMessage: string;
                field: string;
                message: string;
                type: string;
              }[];
            };
          };

          if (
            !typedData.vaultOrgConfirmationSubmit.success &&
            typedData.vaultOrgConfirmationSubmit.errors.length > 0
          ) {
            reject(
              new VaultSubmitError(
                typedData.vaultOrgConfirmationSubmit.errors[0],
              ),
            );
          }

          resolve(typedData.vaultOrgConfirmationSubmit.successData);
        },
        (errors) => {
          reject(
            new VaultSubmitError({
              message: errors.join(', '),
              retryable: false,
              type: 'UnknownResponseError',
            }),
          );
        },
      );
    } else {
      form.submit(
        '/graphql',
        {
          data: requestBodyFormatter({
            kycAddressRegion,
            kycTitle,
            kycPercentOwnership,
            kycIsOwner,
          }),
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        },
        async (responseStatus, { data, errors }) => {
          logger(
            'OrgConfirmationWithCreateKyc vgs form submit data:',
            false,
            responseStatus,
            data,
            errors,
          );
          if (responseStatus !== 200) {
            throw new Error(`Received ${responseStatus} status.`);
          }

          if (kyb.id === undefined) {
            throw new Error('kyb is missing id');
          }

          if (typeof data === 'string') {
            throw new Error(data);
          }

          if (submittedOnYourBehalf) {
            // if this data has been submitted on your behalf
            // we must create both the orgs and provider entities at the orgId level
            // the ancestor kyc will still remain blank and should be resolved at that level
            const providerEntitiesSubmittedOnYourBehalf =
              await createProviderEntities({
                provider,
                kybId: kyb.id,
                kycId: data.kycId,
                stripeEnv,
                statementDescriptor,
                metadata,
              });
            const financialAccountNodeSubmittedOnYourBehalf =
              await createOrgsEntities({
                providerEntities: providerEntitiesSubmittedOnYourBehalf,
                kybId: kyb.id,
                org,
                kycId: data.kycId,
                stripeEnv,
                currentUserId,
                kycIsOwner,
              });
            resolve({
              kybData: {
                kybId: kyb.id,
                org,
                submittedOnYourBehalf: true,
                stripeConnectAccountId:
                  providerEntitiesSubmittedOnYourBehalf.stripeEntities
                    ?.connectAccountId,
                financialAccountId:
                  financialAccountNodeSubmittedOnYourBehalf.properties
                    .finAcctId,
              },
              representative: {
                vaultKycId: data.kycId,
                stripePersonId:
                  providerEntitiesSubmittedOnYourBehalf.stripeEntities
                    ?.personId,
              },
            });
          }

          if (!financialAccountNode.finAcctId) {
            throw new Error(
              `financialAccount ${JSON.stringify(
                financialAccountNode,
              )} is missing finAcctId`,
            );
          }

          const providerKycEntities = await createProviderKycEntities({
            financialAccountNode,
            kycId: data.kycId,
          });

          await financialAccountUserAssociate({
            finAcctId: financialAccountNode.finAcctId,
            userId: currentUserId,
            isBeneficialOwner: kycIsOwner,
            kycId: data.kycId,
            personId: providerKycEntities.stripeEntities?.personId,
            isRepresentative: true,
          });

          resolve({
            kybData: {
              kybId: kyb.id,
              org,
              submittedOnYourBehalf: true,
              stripeConnectAccountId: financialAccountNode.acctId,
              financialAccountId: financialAccountNode.finAcctId,
            },
            representative: {
              vaultKycId: data.kycId,
              stripePersonId: providerKycEntities.stripeEntities?.personId,
            },
          });
        },
        (errors) => {
          reject(
            new VaultSubmitError({
              message: errors.join(', '),
              retryable: false,
              type: 'UnknownResponseError',
            }),
          );
        },
      );
    }
  });
}
